import React from 'react';

import { StyleSheet } from 'react-native';

import { Box } from 'components/ui';
import type { BoxProps } from 'components/ui';
import { layout, primitive, spacing } from 'styles/constants';

export const DEFAULT_PRODUCT_CARD_HEIGHT_MOBILE = 170;
export const DEFAULT_PRODUCT_CARD_HEIGHT_DESKTOP = 190;

type ProductCardContainerProps = BoxProps & {
  $isSelected: boolean;
  $isFlipped?: boolean;
  height?: number;
};

export const ProductCardContainer = ({
  $isSelected,
  $isFlipped,
  height,
  style,
  ...props
}: ProductCardContainerProps) =>
  React.createElement(Box, {
    ...props,
    style: [
      styles.productCardContainer,
      $isSelected ? styles.productCardContainerSelected : styles.productCardContainerDefault,
      $isFlipped ? styles.productCardContainerFlipped : styles.productCardContainerUnflipped,
      height !== undefined && { height },
      style,
    ],
  });

export const ProductCardImageContainer = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, {
    ...props,
    style: [styles.productCardImageContainer, style],
  });

export const ProductCardBadge = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, {
    ...props,
    style: [styles.productCardBadge, style],
  });

export const ProductCardBadgeLeft = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, {
    ...props,
    style: [styles.productCardBadgeLeft, style],
  });

export const ProductCardContent = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, {
    ...props,
    style: [styles.productCardContent, style],
  });

export const ProductNameContainer = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, {
    ...props,
    style: [styles.productNameContainer, style],
  });

export const ProductCardFooter = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, {
    ...props,
    style: [styles.productCardFooter, style],
  });

export const ProductCardOptionsContainer = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, {
    ...props,
    style: [styles.productCardOptionsContainer, style],
  });

export const ProductCardReverseContainer = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, {
    ...props,
    style: [styles.productCardReverseContainer, style],
  });

export const CheckMarkContainer = ({
  $isSelected,
  style,
  ...props
}: BoxProps & { $isSelected: boolean }) =>
  React.createElement(Box, {
    ...props,
    style: [styles.checkMarkContainer, !$isSelected && styles.checkMarkContainerUnselected, style],
  });

const styles = StyleSheet.create({
  productCardContainer: {
    alignItems: 'center',
    backgroundColor: primitive.$white,
    borderRadius: primitive.$borderRadius,
    borderStyle: 'solid',
    borderWidth: 2,
    minHeight: spacing.$40,
    overflow: 'hidden',
  },
  productCardContainerDefault: {
    borderColor: primitive.$blackOpacity10,
  },
  productCardContainerFlipped: {
    padding: spacing.$0,
  },
  productCardContainerSelected: {
    borderColor: primitive.$bbqBrown,
  },
  productCardContainerUnflipped: {
    padding: spacing.$2,
  },
  productCardImageContainer: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    margin: spacing.$0,
    padding: spacing.$0,
    width: layout.full,
  },
  productCardBadge: {
    position: 'absolute',
    right: 2,
    top: 2,
    zIndex: 2,
  },
  productCardBadgeLeft: {
    left: 2,
    position: 'absolute',
    top: 2,
    zIndex: 2,
  },
  productCardContent: {
    height: 60,
    width: layout.full,
  },
  productNameContainer: {
    alignItems: 'center',
    flexGrow: 1,
    justifyContent: 'center',
  },
  productCardFooter: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'space-between',
    width: layout.full,
  },
  productCardOptionsContainer: {
    flex: 1,
    width: layout.full,
  },
  productCardReverseContainer: {
    padding: spacing.$1,
    width: layout['1/3'],
  },
  checkMarkContainer: {
    borderColor: primitive.$blackOpacity20,
    borderRadius: 3,
    position: 'absolute',
    right: spacing.$1_5,
    top: spacing.$1_5,
    zIndex: 9999,
  },
  checkMarkContainerUnselected: {
    borderWidth: 1,
  },
});
